import React, { cloneElement, FC } from 'react';
import {
  List,
  TextField,
  FunctionField,
  useListContext,
  TopToolbar,
  sanitizeListRestProps,
  Pagination,
} from 'react-admin';
import Datagrid from 'components/Datagrid';
import { TnWarrantyFilters } from '../filters/tn-warranty-filters';
import { makeStyles } from '@material-ui/core';
import { exporter } from 'providers/export';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { UIProvider } from 'providers/uiProvider';
import { AppRole } from 'providers/roles';
import CustomExportButton from 'components/CustomExportButton';
import { SupervisorField } from 'components/SupervisorListItems';
import { SKIPPED_PHASE_DISPLAY } from 'utils/phaseUtils';
import { PhaseTypeEnum } from '@vatos-pas/common';
import { dateWithoutServerTimezone } from '../../master-sheet-v2/utils/dateWithoutServerTimezone';

const formatDate = (d: Date): string =>
  `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;

const useStyles = makeStyles({
  content: {
    '@media(max-width: 1550px)': {
      overflow: 'auto',
    },
  },
  contentClosed: {
    '@media(max-width: 1550px)': {
      overflow: 'auto',
    },
  },
  root: {
    '@media(max-width: 1550px)': {
      width: 'calc(100vw - 260px)',
    },
    '@media(max-width: 850px)': {
      width: 'calc(100vw - 45px)',
      marginLeft: '15px',
    },
  },
  rootClosed: {
    '@media(max-width: 1550px)': {
      width: 'calc(100vw - 125px)',
    },
    '@media(max-width: 850px)': {
      width: 'calc(100vw - 45px)',
      marginLeft: '15px',
    },
  },
});

/**
 * Detect if a warranty record is a FinalBluetape (Approved) starting job.
 * These jobs were auto-approved at creation — no contractor was ever assigned.
 * The reliable discriminator is !dateRequested: auto-approved starts skip the
 * request workflow, while normal completions always have a dateRequested.
 * Note: dateApproved CAN be set for auto-approved starts by the backend.
 */
const isFinalBluetapeApprovedStart = (record: any): boolean => {
  if (!record) return false;
  return (
    record.currentPhaseType === PhaseTypeEnum.FinalBluetape &&
    record.currentPhaseApproved === true &&
    !record.currentPhaseContractorName &&
    !record.currentPhaseSuggestedContractorName &&
    !record.currentPhaseDateRequested
  );
};

const ListActions: FC<any> = props => {
  const { className, filters, maxResults, ...rest } = props;
  const {
    currentSort,
    resource,
    displayedFilters,
    filterValues,
    showFilter,
    total,
  } = useListContext();

  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      {filters &&
        cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button',
        })}
      <CustomExportButton
        disabled={total === 0}
        resource={resource}
        sort={currentSort}
        filterValues={filterValues}
        maxResults={maxResults}
      />
    </TopToolbar>
  );
};

export const TnWarrantyList: FC<any> = props => {
  const { hasRole } = PermissionsProvider.useContainer();
  const classes = useStyles(props);
  const { opened } = UIProvider.useContainer();

  const isSupervisor = hasRole(AppRole.Supervisor);

  // Filter for DrywallAndPaint workflow only
  const filterDefault = {
    'phaseStructureType||$eq': 'DrywallAndPaint',
  };

  return (
    <List
      className="list"
      {...props}
      exporter={exporter('tn-warranty-list')}
      filters={<TnWarrantyFilters />}
      sort={{
        field: 'currentPhaseDateApproved',
        order: 'DESC', // Newest to oldest
      }}
      actions={<ListActions maxResults={20000} />}
      classes={{
        content: opened ? classes.content : classes.contentClosed,
        root: opened ? classes.root : classes.rootClosed,
      }}
      filterDefaultValues={filterDefault}
      pagination={<Pagination rowsPerPageOptions={[5, 10, 25, 50]} />}
      perPage={50}
    >
      <Datagrid
        rowClick={(id, _basePath, record) =>
          `/job-master-sheet-v2/${record.jobId || id}`
        }
        isHeaderSelectable={false}
        isRowSelectable={() => false}
      >
        {!isSupervisor && <SupervisorField label="Supervisors" />}
        <TextField source="jobLot" label="Lot" />
        <TextField source="jobBlock" label="Block" />
        <TextField source="builderName" label="Builder" />
        <TextField source="subdivisionName" label="Subdivision" />
        <TextField source="totalSquareFootage" label="Total Sq Ft" />
        <FunctionField
          label="Warranty Start Date"
          render={record => {
            if (isFinalBluetapeApprovedStart(record)) {
              const date = record?.currentPhaseDateApproved;
              if (!date) return <label>{SKIPPED_PHASE_DISPLAY}</label>;
              return (
                <label>{formatDate(dateWithoutServerTimezone(date))}</label>
              );
            }
            const date = record?.currentPhaseDateApproved;
            if (!date) return null;
            return <label>{formatDate(dateWithoutServerTimezone(date))}</label>;
          }}
        />
        <FunctionField
          label="Current Contractor"
          render={record => {
            if (isFinalBluetapeApprovedStart(record)) {
              return <label>{SKIPPED_PHASE_DISPLAY}</label>;
            }
            return <label>{record?.currentPhaseContractorName || ''}</label>;
          }}
        />
        <FunctionField
          label="Last Approval Date"
          render={record => {
            if (isFinalBluetapeApprovedStart(record)) {
              const date = record?.currentPhaseDateApproved;
              if (!date) return <label>{SKIPPED_PHASE_DISPLAY}</label>;
              return (
                <label>{formatDate(dateWithoutServerTimezone(date))}</label>
              );
            }
            const date = record?.currentPhaseDateRequested;
            if (!date) return null;
            return <label>{formatDate(dateWithoutServerTimezone(date))}</label>;
          }}
        />
      </Datagrid>
    </List>
  );
};
